import React, { useEffect, useState } from "react";
import { Lock, FileText, ChevronDown, ChevronUp } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";

/**
 * OutreachTemplatesReadOnly — Step 22.
 *
 * Renders SCRIPT_FACTORY / SCRIPT_RETAIL / SCRIPT_PROVIDER dispatch copy on
 * the User Dashboard as strictly read-only. Users can inspect the wording
 * the AI Recruiter sends on their behalf but have ZERO capability to alter
 * a single character (no <textarea>, no PATCH/PUT exposed on the client).
 * Editing lives exclusively in the /admin panel under super-admin auth.
 */
export default function OutreachTemplatesReadOnly({ style = {} }) {
  const [templates, setTemplates] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/api/tgc/outreach/templates/public`)
      .then((r) => r.json())
      .then((j) => setTemplates(Array.isArray(j.templates) ? j.templates : []))
      .catch(() => setTemplates([]))
      .finally(() => setLoading(false));
  }, []);

  const meta = {
    script_factory:  { color: "#BC13FE", label: "SCRIPT · FACTORY (Enterprise · $250)" },
    script_retail:   { color: "#FFD700", label: "SCRIPT · RETAIL (Merchant $50)" },
    script_provider: { color: "#00F2FF", label: "SCRIPT · PROVIDER (Network Provider $10)" },
  };

  return (
    <div data-testid="outreach-templates-readonly"
      style={{
        background: "#050914", border: "1px solid rgba(0,242,255,0.30)",
        borderRadius: 14, overflow: "hidden", ...style,
      }}>
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "12px 18px",
        background: "linear-gradient(90deg, rgba(255,215,0,0.10), rgba(188,19,254,0.06))",
        borderBottom: "1px solid rgba(255,215,0,0.25)",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <FileText size={16} color="#FFD700" />
          <div>
            <div style={{ fontSize: 13, fontWeight: 800, letterSpacing: 1, color: "#FFD700" }}>
              Шаблоны AI-Рекрутера
            </div>
            <div style={{ fontSize: 10, color: "#5F6884", marginTop: 2 }}>
              Дистанционный найм · Multi-channel outreach copy
            </div>
          </div>
        </div>
        <div data-testid="outreach-templates-lock-badge"
          style={{ display: "flex", alignItems: "center", gap: 6,
                   padding: "4px 10px", borderRadius: 999,
                   background: "rgba(255,61,108,0.10)",
                   border: "1px solid rgba(255,61,108,0.45)" }}>
          <Lock size={12} color="#FF3D6C" />
          <span style={{ fontSize: 10, fontWeight: 800, color: "#FF3D6C", letterSpacing: 1 }}>
            READ-ONLY
          </span>
        </div>
      </div>

      <div style={{ padding: 14 }}>
        {loading && (
          <div style={{ fontSize: 12, color: "#5F6884", padding: 20, textAlign: "center" }}>
            Загрузка шаблонов…
          </div>
        )}
        {!loading && templates.length === 0 && (
          <div style={{ fontSize: 12, color: "#5F6884", padding: 20, textAlign: "center" }}>
            Шаблоны не найдены. Обратитесь к администратору.
          </div>
        )}
        {templates.map((tpl) => {
          const m = meta[tpl.name] || { color: "#8B94B8", label: (tpl.display_name || tpl.name) };
          const open = !!expanded[tpl.name];
          return (
            <div key={tpl.name} data-testid={`outreach-tpl-${tpl.name}`}
              style={{ marginBottom: 8, borderRadius: 10, overflow: "hidden",
                       border: `1px solid ${m.color}44`,
                       background: `linear-gradient(135deg, ${m.color}0d, rgba(255,255,255,0.02))` }}>
              <button onClick={() => setExpanded((s) => ({ ...s, [tpl.name]: !s[tpl.name] }))}
                data-testid={`outreach-tpl-${tpl.name}-toggle`}
                style={{ width: "100%", background: "transparent", border: "none",
                         padding: "10px 14px", display: "flex", alignItems: "center",
                         justifyContent: "space-between", cursor: "pointer", color: "#E7ECFF" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Lock size={11} color={m.color} />
                  <span style={{ fontSize: 12, fontWeight: 800, color: m.color,
                                 letterSpacing: 1, textTransform: "uppercase" }}>
                    {m.label}
                  </span>
                </div>
                {open ? <ChevronUp size={14} color={m.color} /> : <ChevronDown size={14} color={m.color} />}
              </button>
              {open && (
                <div data-testid={`outreach-tpl-${tpl.name}-body`}
                  style={{ padding: "0 14px 14px" }}>
                  <div style={{ fontSize: 10, color: "#5F6884", letterSpacing: 1,
                                textTransform: "uppercase", marginBottom: 4 }}>
                    Subject
                  </div>
                  <div style={{ fontSize: 13, color: "#E7ECFF", marginBottom: 12,
                                padding: 8, borderRadius: 6,
                                background: "rgba(0,0,0,0.30)",
                                border: `1px solid ${m.color}22`, userSelect: "text" }}>
                    {tpl.subject || "—"}
                  </div>
                  <div style={{ fontSize: 10, color: "#5F6884", letterSpacing: 1,
                                textTransform: "uppercase", marginBottom: 4 }}>
                    Body
                  </div>
                  <div style={{ fontSize: 12, color: "#E7ECFF", padding: 10,
                                borderRadius: 6, background: "rgba(0,0,0,0.30)",
                                border: `1px solid ${m.color}22`, whiteSpace: "pre-wrap",
                                lineHeight: 1.5, maxHeight: 220, overflowY: "auto",
                                userSelect: "text" }}>
                    {tpl.body || "—"}
                  </div>
                  <div style={{ marginTop: 10, padding: 8, borderRadius: 6,
                                background: "rgba(255,61,108,0.06)",
                                border: "1px solid rgba(255,61,108,0.30)",
                                fontSize: 11, color: "#FF3D6C",
                                display: "flex", alignItems: "center", gap: 6 }}>
                    <Lock size={11} />
                    {tpl.locked_reason || "Заблокировано для редактирования"}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
